import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactLoading from "react-loading";
import { FlexContainer } from "../ui/flex_container";
import { THEME_COLOR } from "@utils/constants";

export const modernButtonSize = {
    small: "sm",
    medium: "md",
    large: "lg",
};

export function ModernButton({
    text,
    onClick,
    buttonType = "primary",
    icon,
    iconRight,
    image,
    color = {
        start: THEME_COLOR,
        end: THEME_COLOR,
    },
    isBlock,
    isDisabled,
    size = "medium",
    isLoading,
    className,
    ...props
}) {
    // Tryggja að takki passi fyrir myrkt þema
    const [currentTheme, setCurrentTheme] = useState("light");

    // Fylgjast með þemu breytingum
    useEffect(() => {
        // Uppfæra í byrjun
        const updateTheme = () => {
            const dataTheme = document.documentElement.getAttribute("data-theme");
            setCurrentTheme(dataTheme || "light");
        };

        // Uppfæra strax í byrjun
        if (typeof document !== "undefined") {
            updateTheme();

            // Búa til MutationObserver til að fylgjast með þemu breytingum
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === "attributes" && mutation.attributeName === "data-theme") {
                        updateTheme();
                    }
                });
            });

            observer.observe(document.documentElement, { attributes: true });

            return () => observer.disconnect();
        }
    }, []);

    // Reikna út lit fyrir primary buttons
    const getButtonStyle = () => {
        if (buttonType === "primary") {
            return {
                background: `linear-gradient(135deg, ${color.start} 0%, ${color.end} 100%)`,
                color: "white",
                border: "none",
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
            };
        } else {
            // Secondary button
            return {
                backgroundColor: "transparent",
                color: color.start,
                border: `2px solid ${color.start}`,
                boxShadow: "none",
            };
        }
    };

    const buttonStyle = getButtonStyle();
    const textColor = buttonStyle.color;

    return (
        <button
            {...props}
            disabled={isDisabled || isLoading}
            style={{
                ...buttonStyle,
                borderWidth: buttonType === "secondary" ? "2px" : "0",
                borderRadius: 8,
                padding:
                    size === "small" ? "8px 16px" : size === "large" ? "16px 32px" : "12px 24px",
                fontSize: size === "small" ? "14px" : size === "large" ? "18px" : "16px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: isDisabled || isLoading ? "not-allowed" : "pointer",
                transition: "all 0.3s ease",
                opacity: isDisabled || isLoading ? 0.6 : 1,
                ...props.style,
            }}
            onClick={onClick}
            className={classNames("modern-btn", className, {
                [`modern-btn-${buttonType}`]: buttonType,
                [`modern-btn-${size}`]: size,
                "modern-btn-block": isBlock,
                "modern-btn-disabled": isDisabled,
                "dark-theme": currentTheme === "dark",
            })}
            onMouseEnter={(e) => {
                if (!isDisabled && !isLoading) {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                        buttonType === "primary"
                            ? "0 6px 20px rgba(0, 0, 0, 0.3)"
                            : "0 4px 12px rgba(0, 0, 0, 0.15)";
                }
            }}
            onMouseLeave={(e) => {
                if (!isDisabled && !isLoading) {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow =
                        buttonType === "primary" ? "0 4px 15px rgba(0, 0, 0, 0.2)" : "none";
                }
            }}
        >
            {isLoading ? (
                <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    className="d-flex justify-content-center align-items-center text-center"
                    style={{ height: props?.style?.height || 40, position: "relative" }}
                >
                    <ReactLoading type={"bubbles"} color={textColor} width="100px" height="100px" />
                </FlexContainer>
            ) : (
                <>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {image && <img src={image} style={{ width: "1.2rem", height: "1.2rem" }} />}
                    {(icon || image) && text && <span style={{ marginLeft: "10px" }} />}
                    {text}

                    {iconRight && (
                        <FontAwesomeIcon icon={iconRight} style={{ marginLeft: "10px" }} />
                    )}
                </>
            )}
        </button>
    );
}
